// Mirrors the binding contract in .claude/plans/glowskir/wordediting.md §12.3 / §12.4.

export type VersionStatus = "processing" | "ready" | "failed" | "superseded";
export type VersionOrigin = "upload" | "user_save";

export interface VersionInfo {
  id: string;
  document_id: string;
  version_no: number;
  status: VersionStatus;
  created_by: string;
  created_by_name: string;
  created_at: string;
  origin: VersionOrigin;
  pending_change_count: number;
  pending_comment_count: number;
  note: string | null;
  previous_version_id: string | null;
  merged_from_version_id: string | null;
}

export interface AuthoringDocument {
  fse_id: string;
  document_id: string;
  name: string;
  eligible: boolean;
  head: VersionInfo | null;
  pending_version: VersionInfo | null;
  versions: VersionInfo[];
  ai_author: string;
  limits: {
    max_upload_bytes: number;
    max_document_tokens: number;
  };
}

export type MergeChoice = "mine" | "theirs" | "both";

export interface MergeResolution {
  block_id: string;
  choice: MergeChoice;
}

export interface MergeConflict {
  block_id: string;
  part: string;
  base_text: string;
  mine_text: string;
  theirs_text: string;
  theirs_author: string;
}

export interface SaveVersionCreated {
  version: VersionInfo;
  tx_id: string;
  merged?: false;
}

/** 202: the backend merged the head's changes into the saved bytes; reload the version's docx. */
export interface SaveVersionMerged {
  version: VersionInfo;
  tx_id: string;
  merged: true;
}

export type SaveVersionResponse = SaveVersionCreated | SaveVersionMerged;

/** Body of the 409 response. */
export interface SaveVersionConflict {
  detail: string;
  conflicts: MergeConflict[];
  head: VersionInfo;
}

export interface SaveVersionRequest {
  fseId: string;
  file: Uint8Array;
  fileName: string;
  base_version_id: string;
  note?: string;
  resolutions?: MergeResolution[];
  idempotency_key: string;
}

export type AuthoringRunKind = "instructions" | "template";
export type AuthoringRunStatus = "queued" | "running" | "applying" | "repairing" | "done" | "failed" | "cancelled";

export interface AuthoringEdit {
  target_text: string;
  new_text: string;
  comment: string;
  kind: "change" | "comment";
  location: string;
}

export interface AuthoringFailedEdit extends AuthoringEdit {
  error: string;
}

export interface InstructionsRunReport {
  summary: string;
  applied: AuthoringEdit[];
  failed: AuthoringFailedEdit[];
  retried: number;
  author: string;
  revision_ids: string[];
}

export interface TemplateCheckResult {
  heading: string;
  prompt: string;
  summary: string;
  applied: AuthoringEdit[];
  failed: AuthoringFailedEdit[];
  error: string | null;
}

export interface TemplateRunReport {
  results: TemplateCheckResult[];
  author: string;
  revision_ids: string[];
}

export type AuthoringRunReport = InstructionsRunReport | TemplateRunReport;

export const isTemplateRunReport = (report: AuthoringRunReport): report is TemplateRunReport =>
  Array.isArray((report as TemplateRunReport).results);

export interface CreateRunRequest {
  fseId: string;
  file: Uint8Array;
  fileName: string;
  kind: AuthoringRunKind;
  instructions?: string;
  template_id?: string;
}

export interface CreateRunResponse {
  run_id: string;
}

export interface AuthoringRun {
  run_id: string;
  status: AuthoringRunStatus;
  kind: AuthoringRunKind;
  report: AuthoringRunReport | null;
  error: string | null;
  created_at: string;
  finished_at: string | null;
}

/** Frames of `WS /pf-mainweb/document-authoring/ws/{run_id}`. */
export type AuthoringRunFrame =
  | { type: "start"; total: number }
  | { type: "check_done"; index: number; proposed: number }
  | { type: "check_failed"; index: number; error: string }
  | { type: "applying" }
  | { type: "repairing"; count: number }
  | { type: "done"; report: AuthoringRunReport }
  | { type: "error"; message: string };

/** `/ls` addition (§12.4). */
export interface PendingVersionInfo {
  version_no: number;
  tx_id: string | null;
  progress: number | null;
  status: string;
}

export type EligibilityReason = "eligible" | "already_eligible" | "not_docx" | "original_missing" | "not_eligible";

export interface EligibilityCheckResponse {
  eligible: boolean;
  reason: EligibilityReason;
}
